import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { GuidedScheduleEditor } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/GuidedBudgetEditor";
import { useEffect, useState } from "react";

export type ScheduleInput = {
  start_at: string;
  end_at: string;
};

type EditScheduleModalProps = {
  isOpen: boolean;
  isSaving?: boolean;
  /** IANA zone for schedule wall times (must match edit payload). */
  eventTimezone: string;
  initialSchedule: ScheduleInput;
  /** When true, start time cannot be edited and save keeps server start. */
  isStartTimeLocked?: boolean;
  /** When true, start and end cannot be edited (e.g. linked campaign with phased scheduled budget). */
  isScheduleRangeReadOnly?: boolean;
  /** Shown under the schedule when {@link isScheduleRangeReadOnly} is true. */
  scheduleReadOnlyHelperText?: string;
  onClose: () => void;
  onSave: (schedule: ScheduleInput) => void | Promise<void>;
};

export default function EditScheduleModal({
  isOpen,
  isSaving = false,
  eventTimezone,
  initialSchedule,
  isStartTimeLocked = false,
  isScheduleRangeReadOnly = false,
  scheduleReadOnlyHelperText,
  onClose,
  onSave,
}: EditScheduleModalProps) {
  const [schedule, setSchedule] = useState<ScheduleInput>(initialSchedule);

  useEffect(() => {
    if (!isOpen) return;
    setSchedule(initialSchedule);
  }, [initialSchedule, isOpen]);

  const lockFullRange = isScheduleRangeReadOnly === true;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Schedule</ModalHeader>
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {lockFullRange ? null : isStartTimeLocked ? (
              <Text fontSize="sm" color="gray.600">
                Start time cannot be changed for a Meta-synced campaign after
                the schedule has started.
              </Text>
            ) : null}
            <GuidedScheduleEditor
              eventTimezone={eventTimezone}
              startAt={schedule.start_at}
              endAt={schedule.end_at}
              onChangeStartAt={(value) =>
                setSchedule((prev) => ({ ...prev, start_at: value }))
              }
              onChangeEndAt={(value) =>
                setSchedule((prev) => ({ ...prev, end_at: value }))
              }
              isStartAtDisabled={!lockFullRange && isStartTimeLocked}
              isScheduleRangeReadOnly={lockFullRange}
              scheduleReadOnlyHelperText={
                lockFullRange ? scheduleReadOnlyHelperText : undefined
              }
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button
              bg="gray.900"
              color="white"
              _hover={{ bg: "gray.800" }}
              onClick={() =>
                onSave(
                  isStartTimeLocked && !lockFullRange
                    ? { ...schedule, start_at: initialSchedule.start_at }
                    : schedule
                )
              }
              isLoading={isSaving}
              isDisabled={lockFullRange}
            >
              Save
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
